import React from 'react'
import { useTranslation } from 'react-i18next'
import { path, map, join, split, ifElse, not, isNil, length, pathOr, always } from 'ramda'
import * as Icons from '@icons'
import { useAuth, UserPermission } from '@/auth'
import { useOrganizationDisplayLabel } from '@/hooks/useRuntimeProps'

const { compose } = require('ramda')

export type RouteItem = {
  id: number
  title: string
  path: string
  component: typeof React.Component
  icon?: React.FunctionComponent
  disabled?: boolean
  param?: string
  children?: RouteItem[]
  hide?: RouteItem[]
  creatable?: boolean
  updatable?: boolean
  deletable?: boolean
  readable?: boolean
  isSubMenu: boolean
  code: string
  mainKey: string
  subKey: string
  parentName?: string
  url: string
  name?: string
  isSubMenuIcon?: boolean // 顯示 icon 判斷
  // 相容 iso/ ghg 頁面標題, 依據 url 判斷
  pageTitle?: string
}

export interface Route extends RouteItem {
  children: Array<RouteItem>
  hide: Array<RouteItem>
  disabled: boolean
}

let setting = {
  creatable: true,
  deletable: true,
  isSubMenu: true,
  readable: true,
  updatable: true,
}

const ACT_SCOPE3 = 'ACT_SCOPE3'

export function usePrepareRoutes() {
  const { user } = useAuth()
  const { t } = useTranslation()

  const organizationDisplayLabel = useOrganizationDisplayLabel()

  let _user = user as any

  return (routes: { [key: string]: any }) => {
    const __getGHGScope3Component = (p: any) => {
      return path([
        compose(
          join(''),
          map((s: string) => s.charAt(0).toUpperCase() + s.slice(1)),
          split('-'),
          ifElse(
            v => length(v) > 3,
            v => pathOr('', [length(v) - 2])(v),
            pathOr('', [1])
          ),
          split('/')
        )(p),
      ])(routes)
    }

    const __getComponent = (p: any, mainKey: string) => {
      if (mainKey !== ACT_SCOPE3) {
        return path([
          compose(
            join(''),
            map((s: string) => s.charAt(0).toUpperCase() + s.slice(1)),
            split('-'),
            ifElse(
              v => length(v) > 3,
              v => pathOr('', [length(v) - 1])(v),
              pathOr('', [1])
            ),
            split('/')
          )(p),
        ])(routes)
      }

      return __getGHGScope3Component(p)
    }

    const funcList = compose(
      map((x: UserPermission) => ({
        ...x,
        title: x.isSubMenu
          ? t(`menu:${x.mainKey}`, { organizationDisplayLabel })
          : // 第一層抓 code, 顯示標題
            t(`menu:${x.code}`, { organizationDisplayLabel }),
        path: x.url,
        icon: ifElse(
          compose(not, isNil, path([x.icon])),
          () => (Icons as any)[x.icon],
          always(null)
        )(Icons),
        ...(x.isSubMenu
          ? {}
          : {
              component: __getComponent(pathOr('', ['url'], x), x.mainKey),
              // hide: __getHide(
              //   pathOr('', ['url'], x),
              //   __getComponent(pathOr('', ['url'], x))
              // ),
            }),
        children: compose(
          map((c: UserPermission) => {
            const component = __getComponent(pathOr('', ['url'], c), c.mainKey)
            const lastPath = c.url.split('/').pop()
            //-- 原來的邏輯
            const obj = {
              ...c,
              id: c.code,
              title: t(`menu:${lastPath}`, { organizationDisplayLabel }),
              path: c.url,
              component,
              // 頁面標題
              pageTitle: t(`menu:${lastPath}`),
              // hide: __getHide(c.url, component),
            }

            if (c.mainKey !== ACT_SCOPE3) {
              return obj
            }

            // 範疇三
            const leafTitle = t(`menu:${c.url.split('/').slice(-2, -1)}`)

            return {
              ...obj,
              id: lastPath,
              // 菜單標題
              title: c.isSubMenuIcon ? leafTitle : t(`menu:${lastPath}`),
              // 頁面標題
              pageTitle: c.originalPath || c.isSubMenuIcon ? leafTitle : t(`menu:${lastPath}`),
            }
          }),
          pathOr([], ['children'])
        )(x),
      })),
      pathOr([], ['funcList'])
    )(_user)

    return funcList
  }
}

export default usePrepareRoutes
